import { useFocusRing } from '@react-native-aria/focus'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Pressable, View } from 'react-native'

import type { ICON } from '@/src/general/enums/icons'
import { dataFocusVisibleClasses } from '@/src/styles/classes'

import { Icon } from '../media/Icon'
import { Switch, type TSwitchProps } from '../Switch'
import { Typography } from '../text/Typography'

export type TSwitchInputProps = {
  label?: string
  icon?: ICON
  className?: string
  disabled?: boolean
  testID?: string
} & TSwitchProps

export const SwitchInput = ({
  label,
  icon,
  disabled,
  className,
  testID,
  ...switchProps
}: TSwitchInputProps): ReactNode => {
  const { isFocusVisible, focusProps } = useFocusRing()

  const handlePress = () => {
    if (disabled) return
    const isToggledOn = !switchProps.value
    switchProps.onValueChange?.(isToggledOn)
  }

  return (
    <Pressable
      onPress={handlePress}
      testID={testID}
      className={clsx(
        dataFocusVisibleClasses,
        'flex flex-row items-center justify-between gap-4 rounded-md px-1 py-2',
        className,
      )}
      dataSet={{ 'focus-visible': isFocusVisible }}
      {...focusProps}
    >
      <View className={clsx('flex flex-1 flex-row items-start gap-4', disabled && 'opacity-50')}>
        {icon && (
          <Typography className="flex" ariaRole="div">
            <Icon name={icon} />
          </Typography>
        )}

        <Typography ariaRole="span">{label}</Typography>
      </View>
      <View className="flex h-[24px] items-center justify-center">
        <Switch
          testID={`${testID}-checkbox`}
          value={switchProps.value}
          onValueChange={switchProps.onValueChange}
          focusable={false}
          disabled={disabled}
        />
      </View>
    </Pressable>
  )
}
